import instancePDF from 'barebones-pdf';

import {
    CHAIR_RADIUS_SEATS_LABELS_PDF_FACTOR,
    CHAIR_RADIUS_SECTIONS_LABELS_PDF_FACTOR,
    CHAIR_RADIUS_PDF_FACTOR,
    CIRCLE_DIAMETER_PDF_FACTOR,
    PDF_HEIGHT,
    TABLE_COLOR,
    TEXT_RATIO,
} from '../../constants/pdf-constants';
import {
    LOCKED_CHAIR_COLOR,
    RENDER_OPTION_AVAILABILITY,
    RENDER_OPTION_HOLDS,
    RENDER_OPTION_TIERS_COLORS,
    STENCIL_COLOR,
    THUMBNAIL_LOCKED_CHAIR_COLOR,
} from '../../constants/render-constants';
import {
    STATUS_PROTECTED,
    STATUS_RESERVED,
} from '../../constants/status-constants';
import { getChairRadiusInDisplayCoords } from '../chairs-renderer-helper';
import { getSeatStatus } from '../seat-status-helper';

const instancePDFRenderer = () => {
    // We create a new pdf page here

    const circleDict = {};

    const createRenderSurface = ({ width, height }) => {
        const pdf = instancePDF();

        pdf.addPage({});

        return {
            width,
            height,
            pdf,
        };
    };

    const getCircleShape = (renderSurface, r) => {
        let shape = circleDict[r];

        if (!shape) {
            shape = {
                type: 'circle',
                r,
            };
            circleDict[r] = shape;
        }
        return shape;
    };

    /*
        It makes sense to cache circles, as they do not change with orientation and they are normally always the
        same size. Therefore, we have a getCircleShape and a drawShape function.

        In the case of rectangles, the variations are too big to cache, as we can have different widths and heights
        and also different angles.
    */
    /*eslint-disable*/
    const drawRectangle = (
        renderSurface,
        cx,
        cy,
        width,
        height,
        theta,
        color,
    ) => {
        const { pdf } = renderSurface;

        pdf.setStrokeColor(color);
        pdf.setFillColor(color);
        pdf.addRectangle(
            cx,
            PDF_HEIGHT - cy,
            width,
            height,
            -theta,
            true,
            true,
        );
    };

    const drawOval = (renderSurface, cx, cy, width, height, theta, color) => {
        const { pdf } = renderSurface;

        pdf.setStrokeColor(color);
        pdf.setFillColor(color);
        pdf.addOval(cx, PDF_HEIGHT - cy, width, height, -theta, true, true);
    };

    const drawCircle = (renderSurface, cx, cy, diameter, color) => {
        const { pdf } = renderSurface;

        pdf.setStrokeColor(color);
        pdf.setFillColor(color);
        pdf.addCircle(
            cx,
            PDF_HEIGHT - cy,
            diameter * CIRCLE_DIAMETER_PDF_FACTOR,
            true,
            true,
        );
    };

    const drawTexturedRectangle = (
        renderSurface,
        cx,
        cy,
        rectWidth,
        rectHeight,
        theta,
        stencilRecord,
    ) => {
        const { pdf } = renderSurface;
        const {
            originalImageData: { data, height, width },
            id,
        } = stencilRecord;

        pdf.addImage({
            name: id,
            resource: data,
            size: {
                width,
                height,
            },
            format: 'RGBA',
        });
        pdf.paintImage({
            name: id,
            dst: {
                cx,
                cy: PDF_HEIGHT - cy,
                width: rectWidth,
                height: rectHeight,
                angle: theta,
            },
        });
    };

    const drawShape = (renderSurface, cx, cy, shape, color) => {
        const { type, r } = shape;

        if (type === 'circle') {
            drawCircle(renderSurface, cx, cy, r * 2, color);
        }
    };

    const drawPolygon = (renderSurface, vertices, color) => {
        const { pdf } = renderSurface;

        // We need at least 3 vertices for a polygon
        if (vertices.length < 3) {
            return;
        }

        const pathVertices = [];

        for (let i = 0; i < vertices.length; i += 2) {
            pathVertices.push({
                x: vertices[i],
                y: vertices[i + 1],
            });
        }

        pdf.setFillColor(color);
        pdf.addPolygon({
            path: pathVertices,
            closed: true,
            fill: true,
            stroke: false,
        });
    };

    const drawLine = (renderSurface, x0, y0, x1, y1, thickness, color) => {
        const dx = x1 - x0;
        const dy = y1 - y0;
        const l = Math.sqrt(dx * dx + dy * dy);

        if (!l) {
            return;
        }

        // We calculate the displacement vectors
        const t2 = thickness * 0.5;
        const vx = (-dy * t2) / l;
        const vy = (dx * t2) / l;
        const vertices = [
            x0 - vx,
            PDF_HEIGHT - (y0 - vy),
            x0 + vx,
            PDF_HEIGHT - (y0 + vy),
            x1 + vx,
            PDF_HEIGHT - (y1 + vy),
            x1 - vx,
            PDF_HEIGHT - (y1 - vy),
        ];

        drawPolygon(renderSurface, vertices, color);
    };

    const getImageData = (renderSurface) => {
        const { width, height } = renderSurface;

        const resultImageData = {
            width,
            height,
            pdf: renderSurface.pdf,
            data: new Uint8Array(width * height * 4),
        };

        return resultImageData;
    };

    const drawText = (renderSurface, textRecord) => {
        const { pdf } = renderSurface;
        const { cx, cy, height, text, color, width } = textRecord;
        const minx = cx - width / 2;
        const maxx = cx + width / 2;
        const miny = PDF_HEIGHT - (cy - height / 2);
        const maxy = PDF_HEIGHT - (cy + height / 2);
        const sizeBase = Math.min(width, height);

        // NOTE:/EB-196582 - In some cases text can be undefined.
        // Barebones-pdf fails to catch this and throws an error.
        // Out of precaution we add a check here and only add
        // the text if the text is defined.
        if (text) {
            pdf.setFillColor(color);
            pdf.setStrokeColor(color);
            pdf.addText({
                text: text,
                font: 'helvetica',
                size: sizeBase * TEXT_RATIO,
                box: {
                    minx,
                    maxx,
                    miny,
                    maxy,
                },
                alignment: 'cc',
            });
        }
    };

    const getTableColor = () => TABLE_COLOR;

    const getChairRadius = (modelViewMatrix) =>
        getChairRadiusInDisplayCoords(CHAIR_RADIUS_PDF_FACTOR, modelViewMatrix);

    // NOTE: We use the chair radius as the base sections label size to keep a consistent relation between them.
    const getSectionLabelsSize = (modelViewMatrix) =>
        getChairRadius(modelViewMatrix) *
        CHAIR_RADIUS_SECTIONS_LABELS_PDF_FACTOR;

    // NOTE: We use the chair radius as the base seats label size to keep a consistent relation between them.
    const getSeatLabelsSize = (modelViewMatrix) =>
        getChairRadius(modelViewMatrix) * CHAIR_RADIUS_SEATS_LABELS_PDF_FACTOR;

    const getColorFromIdAndTier = ({
        id,
        tierDictionary,
        tierNumber,
        statusDictionary,
        renderOptions,
        defaultTierColor,
    }) => {
        const tierRecord = tierDictionary[tierNumber];
        const statusRecord = statusDictionary[id];
        let tierColor = tierRecord ? tierRecord.color : defaultTierColor;
        let seatColor = STENCIL_COLOR;

        if (!statusRecord) {
            if (renderOptions[RENDER_OPTION_TIERS_COLORS]) {
                seatColor = tierColor;
            }

            return seatColor;
        }
        const seatStatus = getSeatStatus(statusRecord);

        // We first check if the tier colors option is enabled
        if (renderOptions[RENDER_OPTION_TIERS_COLORS]) {
            seatColor = tierColor;
        }

        // Then we check the availability option
        if (
            renderOptions[RENDER_OPTION_AVAILABILITY] &&
            seatStatus === STATUS_RESERVED
        ) {
            seatColor = THUMBNAIL_LOCKED_CHAIR_COLOR;
        }

        // Finally we check the render holds option
        if (
            renderOptions[RENDER_OPTION_HOLDS] &&
            seatStatus === STATUS_PROTECTED
        ) {
            // If the seat render option is enabled, we use a dark grey color
            seatColor = LOCKED_CHAIR_COLOR;
        }

        return seatColor;
    };

    return {
        createRenderSurface,
        getChairRadius,
        getCircleShape,
        getColorFromIdAndTier,
        drawShape,
        drawRectangle,
        drawOval,
        drawTexturedRectangle,
        drawPolygon,
        drawLine,
        drawText,
        getImageData,
        getSeatLabelsSize,
        getSectionLabelsSize,
        getTableColor,
    };
};

export default instancePDFRenderer;
